import { strToSnakeCase } from "@/helpers/stringProcessors";
import {
    mdiCheckCircleOutline,
    mdiCloseCircleOutline,
    mdiReceiptTextClockOutline,
    mdiCreditCardRefundOutline,
} from "@mdi/js";
import { useOrganisationStore } from "@/plugins/store";

/**
 * Maps the payment status. The list is combined of the list from the api
 * (filepath: api/app/common/constants/enums.py TransactionStatus) and the
 * v1 frontend (filepath: frontend/src/mixins/getStatusApiCall.ts )
 * @param {string} status
 * @returns {string}
 */
export const mapPaymentStatus = (status: string): string => {
    const formattedStatus = status.toLowerCase();
    const statusMapping = {
        SUCCESS: ["success", "requires_payout", "requires_conversion"],
        FAILED: ["declined", "rejected", "failed", "cancelled", "abandoned"],
        PROCESSING: [
            "requires_payout",
            "requires_conversion",
            "authorised",
            "submitted",
            "pending",
            "processing",
            "created",
            "authorization_required",
            "authorizing",
            "authorized",
            "executed",
            "capture_blocked",
            "capture_requested",
        ],
        ACCEPTED: ["acsc_confirmed"],
        REFUNDED: ["refunded"],
        UNKNOWN: ["unknown"],
    };

    const mappedStatus = Object.keys(statusMapping).find((key) =>
        statusMapping[key].includes(formattedStatus),
    );

    return mappedStatus !== undefined ? mappedStatus : "-";
};

export const mapPaymentStatusDescription = (
    status: string,
    paymentMethodCategory: string,
) => {
    /**
     * Maps the payment status to a description reference. The description text
     * is defined in the vi18n plugin for localisation purposes
     * (filepath: ../plugins/vi18n/index.ts)
     * @param {[string]} status
     * @returns {[string]}
     */
    const mappedStatus = mapPaymentStatus(status);
    const processing =
        paymentMethodCategory === "bank_transfer"
            ? "processingBankTransfer"
            : "processing";

    const statusMapping = {
        SUCCESS: "statusDescriptions.success",
        FAILED: `statusDescriptions.failed`,
        PROCESSING: `statusDescriptions.${processing}`,
        ACCEPTED: "statusDescriptions.accepted",
        REFUNDED: "statusDescriptions.refunded",
    };

    return mappedStatus !== undefined && mappedStatus in statusMapping
        ? statusMapping[mappedStatus]
        : "statusDescriptions.default";
};

export const getStatusIcon = (status: string) => {
    /**
     * Maps the payment status to an mdi-icon
     * @param {[string]} status
     * @returns {[string]}
     */

    const mappedStatus = mapPaymentStatus(status);
    const iconMapping = {
        SUCCESS: mdiCheckCircleOutline,
        FAILED: mdiCloseCircleOutline,
        PROCESSING: mdiReceiptTextClockOutline,
        ACCEPTED: mdiReceiptTextClockOutline,
        REFUNDED: mdiCreditCardRefundOutline,
    };

    return mappedStatus !== undefined && mappedStatus in iconMapping
        ? iconMapping[mappedStatus]
        : "";
};

export const getStatusIconColor = (status: string) => {
    /**
     * Maps the payment status icon to a color. Success maps to success green,
     * Failed maps to alert red, while the rest map to an info blue.
     * @param {[string]} status
     * @returns {[string]}
     */

    const mappedStatus = mapPaymentStatus(status);
    switch (mappedStatus) {
        case "SUCCESS":
            return "success";
        case "FAILED":
            return "error";
        default:
            return "info";
    }
};

export const showSupportEmail = (status: string) => {
    /**
     * Display the Esenda support email next to the status
     * description if the status is either in the Failed category or
     * the Unknown category.
     * @param {[string]} status
     * @returns {[boolean]}
     */
    const mappedStatus = mapPaymentStatus(status);

    return mappedStatus !== undefined
        ? ["FAILED", "UNKNOWN"].includes(mappedStatus)
        : false;
};

export const isTerminalStatus = (status: string) => {
    /**
     * Checks if the payment status is in the list of
     * terminal statuses: ["SUCCESS", "FAILED", "REFUNDED"].
     * @param {[string]} status
     * @returns {[boolean]}
     */
    const mappedStatus = mapPaymentStatus(status);
    return ["SUCCESS", "FAILED", "REFUNDED"].includes(mappedStatus);
};

export const getSupplementaryDataLabel = (keyName: string) => {
    /**
     * Returns the display name of a value from the form structure object
     * when given the value of keyName
     * @param {[string]} keyName
     * @returns {[boolean]}
     */
    const orgStore = useOrganisationStore();

    const formFields = orgStore.formStructure?.formFields;
    const translate = orgStore.formStructure?.translatableLabels ?? false;

    if (formFields == undefined || formFields === null) return "";
    else {
        const formattedKeyName = strToSnakeCase(keyName);
        const field = formFields.find(
            (field) => field.keyName === formattedKeyName,
        );
        const label = field ? field.displayName : "";
        if (label === "") return "";
        return translate ? `labels.dynamic.${label}` : label;
    }
};

export const isStatusNotCreated = (status: string) => {
    /**
     * Checks if the payment status is not CREATED
     * @param {[string]} status
     * @returns {[boolean]}
     */
    const formattedStatus = status.toUpperCase();
    return !["CREATED", "UNDEFINED"].includes(formattedStatus);
};
